import React from 'react';
import { services, Service } from '../data/services';
import { standorte, zusatzgebiete } from '../data/standorte';
import { socialImagePath } from '../shared/media';
import { SITE_URL, SITE_NAME, absoluteUrl } from './Seo';

/**
 * Strukturierte Daten (JSON-LD) fuer Suchmaschinen.
 *
 * Die Angaben werden aus denselben Quellen erzeugt, aus denen auch die Seite
 * gerendert wird - `services.tsx` und `standorte.ts`. Vorher stand das JSON-LD
 * als fester Block in public/index.html und war bereits veraltet: es nannte
 * zwei Einsatzorte, waehrend die Seite neunzehn auflistet.
 *
 * Bewusst NICHT enthalten ist `aggregateRating`. Google schliesst
 * LocalBusiness und alle Organization-Subtypen (dazu zaehlt CleaningService)
 * von Sterne-Rich-Results aus, wenn das bewertete Unternehmen die Bewertungen
 * auf der eigenen Seite kontrolliert, und untersagt ausdruecklich das
 * Uebernehmen fremder Bewertungen ("Don't aggregate reviews or ratings from
 * other websites"). Die Google-Rezensionen im Seitenfuss hier auszuzeichnen
 * brachte also keine Sterne, waere aber ein Verstoss gegen die Richtlinien.
 * https://developers.google.com/search/docs/appearance/structured-data/review-snippet
 *
 * JSON-LD wird im Body gerendert - die Position im Dokument spielt fuer die
 * Auswertung keine Rolle, und so landet es zuverlaessig im Prerender-Snapshot.
 */

const ORGANIZATION_ID = `${SITE_URL}/#organization`;

const JsonLd: React.FC<{ data: unknown }> = ({ data }) => (
  <script
    type="application/ld+json"
    // JSON.stringify liefert bereits gueltiges JSON; `<` wird maskiert, damit
    // ein Textinhalt das script-Tag nicht vorzeitig schliessen kann.
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }}
  />
);

/** Das Unternehmen selbst. Steht auf jeder Seite, immer unter derselben @id. */
export const OrganizationSchema: React.FC = () => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'CleaningService',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    description:
      'Spezialisierte Praxisreinigung sowie Büro-, Treppenhaus-, Glas- und Sonder- & Anlassreinigung '
      + 'in Bielefeld, Gütersloh und Umgebung.',
    url: `${SITE_URL}/`,
    // Ein echtes Foto statt des Logos: Google erwartet unter `image` eine
    // Abbildung des Betriebs, nicht die Wortmarke.
    image: absoluteUrl(socialImagePath('praxisreinigung')),
    logo: `${SITE_URL}/logo.png`,
    telephone: '+49 172 9182451',
    email: 'info@apcleanservices.de',
    founder: { '@type': 'Person', name: 'Abimanju Premachandran' },
    vatID: 'DE457367373',
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Moltkestr. 20',
      postalCode: '33330',
      addressLocality: 'Gütersloh',
      addressRegion: 'Nordrhein-Westfalen',
      addressCountry: 'DE',
    },
    // Aus standorte.ts, damit Karte, Ortsliste und Schema nicht auseinanderlaufen.
    areaServed: [...standorte, ...zusatzgebiete].map((o) => ({ '@type': 'City', name: o.name })),
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Reinigungsleistungen',
      itemListElement: services.map((s) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          '@id': `${SITE_URL}/${s.slug}#service`,
          name: s.title,
          description: s.shortDesc,
        },
      })),
    },
    sameAs: ['https://instagram.com/ap_cleanservices', 'https://tiktok.com/@ap_cleanservices'],
  };
  return <JsonLd data={data} />;
};

/** Eine einzelne Leistung plus Breadcrumb-Pfad und ggf. FAQ, nur auf den Detailseiten. */
export const ServiceSchema: React.FC<{ service: Service }> = ({ service }) => {
  const url = absoluteUrl(`/${service.slug}`);
  const data: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${url}#service`,
      name: service.title,
      description: service.seo.description,
      serviceType: service.title,
      url,
      image: absoluteUrl(socialImagePath(service.image)),
      provider: { '@id': ORGANIZATION_ID },
      areaServed: standorte.map((o) => ({ '@type': 'City', name: o.name })),
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: service.title,
        itemListElement: service.items.map((item) => ({
          '@type': 'Offer',
          itemOffered: { '@type': 'Service', name: item },
        })),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Startseite', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Leistungen', item: absoluteUrl('/leistungen') },
        { '@type': 'ListItem', position: 3, name: service.title, item: url },
      ],
    },
  ];
  if (service.faqs && service.faqs.length > 0) {
    // Google zeigt FAQ-Rich-Results seit 2023 nur noch fuer Behoerden- und
    // Gesundheitsseiten. Das Markup bleibt trotzdem sinnvoll: Es ordnet Frage
    // und Antwort eindeutig zu, was Suchmaschinen und KI-Assistenten beim
    // Zitieren auswerten. Inhalt identisch mit der sichtbaren FAQ-Sektion,
    // wie die Richtlinien es verlangen.
    // https://developers.google.com/search/docs/appearance/structured-data/faqpage
    data.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: service.faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }
  return <JsonLd data={data} />;
};
